import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Save,
  CheckCircle2,
  Undo2,
  ArrowLeft,
  ClipboardCheck,
  Trash2,
  Eye,
  FileDown,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Switch } from './ui/switch';

const todayISODate = () => new Date().toISOString().slice(0, 10);

const formatVisitDate = (iso) => {
  if (!iso || typeof iso !== 'string') return '—';
  const p = iso.slice(0, 10);
  if (p.length !== 10) return iso;
  const [y, m, d] = p.split('-');
  return `${d}/${m}/${y}`;
};

/** Note globale : points obtenus / total possible (3 pts par critère dans scores). */
const formatNoteCell = (c) => {
  let sum = c.score_sum;
  let max = c.score_max;
  const raw = c.scores;
  if ((sum == null || max == null || max <= 0) && raw && typeof raw === 'object') {
    const keys = Object.keys(raw);
    if (keys.length > 0) {
      max = keys.length * 3;
      sum = 0;
      for (const v of Object.values(raw)) {
        const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
        if (Number.isNaN(n)) continue;
        const r = Math.round(n);
        if (r >= 0 && r <= 3) sum += r;
      }
    }
  }
  if (sum != null && max != null && max > 0) {
    return `${sum} / ${max}`;
  }
  return '—';
};

/** Affichage lecture seule d’un contrôle validé (modal). */
const ValidatedControlReadOnly = ({ detail, theme }) => {
  const formThemes = detail?.form?.themes || [];
  const sc = detail?.scores || {};
  const tc = detail?.theme_comments || {};
  const cc = detail?.criterion_comments || {};

  return (
    <div className="space-y-4 text-left">
      <div
        className="rounded-lg border px-3 py-2 text-sm flex flex-wrap gap-3"
        style={{ borderColor: theme.border, color: theme.textSecondary }}
      >
        <span>
          <strong style={{ color: theme.text }}>Date :</strong> {formatVisitDate(detail?.control_date)}
        </span>
        <span>
          <strong style={{ color: theme.text }}>Entreprise :</strong>{' '}
          {detail?.entreprise_nom_snapshot || '—'}
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          Validé
          {detail?.validated_at && (
            <span className="text-xs opacity-80">
              ({new Date(detail.validated_at).toLocaleString('fr-FR')})
            </span>
          )}
        </span>
      </div>

      {!detail?.form && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Le formulaire source n’est plus disponible ; les notes enregistrées sont affichées ci-dessous
          lorsque possible.
        </p>
      )}

      {formThemes.map((th) => (
        <div
          key={th.id}
          className="rounded-xl border p-4 space-y-3"
          style={{ borderColor: theme.border, backgroundColor: theme.background }}
        >
          <h3 className="font-semibold" style={{ color: theme.text }}>
            {th.name || 'Thème'}
          </h3>
          {(tc[th.id] || '').trim() ? (
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Commentaire thème
              </p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: theme.text }}>
                {tc[th.id]}
              </p>
            </div>
          ) : null}
          {(th.criteria || []).length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent" style={{ borderColor: theme.border }}>
                  <TableHead style={{ color: theme.text }}>Critère</TableHead>
                  <TableHead className="w-24" style={{ color: theme.text }}>
                    Note
                  </TableHead>
                  <TableHead style={{ color: theme.text }}>Commentaire</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(th.criteria || []).map((cr) => {
                  const raw = sc[cr.id];
                  const n = raw !== undefined && raw !== '' ? Number(raw) : null;
                  const noteLabel =
                    n !== null && !Number.isNaN(n) ? `${n} / 3` : '—';
                  return (
                    <TableRow key={cr.id} style={{ borderColor: theme.border }}>
                      <TableCell className="align-top font-medium" style={{ color: theme.text }}>
                        {cr.name || '—'}
                      </TableCell>
                      <TableCell className="align-top tabular-nums font-semibold" style={{ color: theme.text }}>
                        {noteLabel}
                      </TableCell>
                      <TableCell className="align-top text-sm" style={{ color: theme.text }}>
                        {(cc[cr.id] || '').trim() || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      ))}

      {formThemes.length === 0 && Object.keys(sc).length > 0 && (
        <div className="rounded-lg border p-3 text-sm" style={{ borderColor: theme.border }}>
          <p className="font-medium mb-2" style={{ color: theme.text }}>
            Notes enregistrées
          </p>
          <ul className="space-y-1" style={{ color: theme.textSecondary }}>
            {Object.entries(sc).map(([id, v]) => (
              <li key={id}>
                Critère {id} : {String(v)} / 3
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ControleQualiteTab = ({ token, apiBase, theme, onUnauthorized, isActive }) => {
  const [controlsList, setControlsList] = useState([]);
  const [formsList, setFormsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState(null);
  const [mode, setMode] = useState('list');
  const [detail, setDetail] = useState(null);

  const [newFormId, setNewFormId] = useState('');
  const [newDate, setNewDate] = useState(todayISODate);
  const [newEntId, setNewEntId] = useState('');
  const [newEntNom, setNewEntNom] = useState('');

  const [editDate, setEditDate] = useState('');
  const [editEntId, setEditEntId] = useState('');
  const [editEntNom, setEditEntNom] = useState('');
  const [scores, setScores] = useState({});
  const [themeComments, setThemeComments] = useState({});
  const [criterionComments, setCriterionComments] = useState({});

  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [viewModalId, setViewModalId] = useState(null);
  const [viewModalDetail, setViewModalDetail] = useState(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);

  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [pdfExportControl, setPdfExportControl] = useState(null);
  const [pdfGlobalComment, setPdfGlobalComment] = useState('');
  const [pdfIncludeCriteriaTable, setPdfIncludeCriteriaTable] = useState(true);
  const [pdfExporting, setPdfExporting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Une erreur est survenue';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadControls = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/qc-controls`, {
        headers: authHeaders,
      });
      setControlsList(Array.isArray(data) ? data : []);
    } catch (e) {
      handleApiError(e);
    } finally {
      setListLoading(false);
    }
  }, [apiBase, token, handleApiError]);

  const loadForms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/admin/qc-forms`, {
        headers: authHeaders,
      });
      setFormsList(Array.isArray(data) ? data : []);
    } catch (e) {
      handleApiError(e);
    }
  }, [apiBase, token, handleApiError]);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/admin/sites`, {
        headers: authHeaders,
      });
      setSitesList(Array.isArray(data) ? data : []);
    } catch (e) {
      handleApiError(e);
    }
  }, [apiBase, token, handleApiError]);

  useEffect(() => {
    if (!isActive) return;
    loadControls();
    loadForms();
    loadSites();
  }, [isActive, loadControls, loadForms, loadSites]);

  useEffect(() => {
    if (mode === 'new' && formsList.length > 0) {
      setNewFormId((prev) => prev || formsList[0].id);
    }
  }, [mode, formsList]);

  const syncEditorFromDetail = useCallback((d) => {
    if (!d) return;
    setEditDate(d.control_date || '');
    setEditEntId(d.entreprise_id || '');
    setEditEntNom(d.entreprise_nom_snapshot || '');
    const sc = {};
    Object.entries(d.scores || {}).forEach(([k, v]) => {
      sc[k] = typeof v === 'number' ? v : Number(v);
    });
    setScores(sc);
    setThemeComments({ ...(d.theme_comments || {}) });
    setCriterionComments({ ...(d.criterion_comments || {}) });
  }, []);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      try {
        const { data } = await axios.get(`${apiBase}/admin/qc-controls/${id}`, {
          headers: authHeaders,
        });
        setDetail(data);
        syncEditorFromDetail(data);
        if (!data.form) {
          toast.error('Formulaire modèle introuvable : impossible d’afficher les critères.');
        }
      } catch (e) {
        handleApiError(e);
        setSelectedControlId(null);
        setMode('list');
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [apiBase, token, handleApiError, syncEditorFromDetail]
  );

  useEffect(() => {
    if (!isActive || !selectedControlId) {
      if (!selectedControlId) setDetail(null);
      return;
    }
    loadDetail(selectedControlId);
  }, [isActive, selectedControlId, loadDetail]);

  useEffect(() => {
    if (!isActive || !viewModalId) {
      if (!viewModalId) setViewModalDetail(null);
      return;
    }
    let alive = true;
    setViewModalLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/admin/qc-controls/${viewModalId}`, {
          headers: authHeaders,
        });
        if (alive) setViewModalDetail(data);
      } catch (e) {
        if (alive) {
          handleApiError(e);
          setViewModalId(null);
        }
      } finally {
        if (alive) setViewModalLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isActive, viewModalId, apiBase, handleApiError]);

  const draftControls = useMemo(
    () => controlsList.filter((c) => c.status !== 'validated'),
    [controlsList]
  );

  const validatedControls = useMemo(
    () =>
      [...controlsList.filter((c) => c.status === 'validated')].sort((a, b) =>
        (b.control_date || '').localeCompare(a.control_date || '')
      ),
    [controlsList]
  );

  useEffect(() => {
    if (!selectedControlId || mode !== 'edit') return;
    const row = controlsList.find((x) => x.id === selectedControlId);
    if (row && row.status === 'validated') {
      setSelectedControlId(null);
      setDetail(null);
      setMode('list');
    }
  }, [controlsList, selectedControlId, mode]);

  const surfaceStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.border,
  };

  const buildScoresPayload = () => {
    const out = {};
    Object.entries(scores).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      const n = Number(v);
      if (!Number.isNaN(n)) out[k] = n;
    });
    return out;
  };

  const putControl = async (payload) => {
    if (!selectedControlId) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${apiBase}/admin/qc-controls/${selectedControlId}`,
        payload,
        { headers: authHeaders }
      );
      setDetail(data);
      syncEditorFromDetail(data);
      toast.success(
        payload.status === 'validated'
          ? 'Contrôle validé officiellement'
          : 'Brouillon enregistré'
      );
      await loadControls();
      if (payload.status === 'validated') {
        setSelectedControlId(null);
        setDetail(null);
        setMode('list');
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => {
    putControl({
      control_date: editDate,
      entreprise_id: editEntId,
      entreprise_nom: editEntNom,
      scores: buildScoresPayload(),
      theme_comments: themeComments,
      criterion_comments: criterionComments,
      status: 'draft',
    });
  };

  const handleValidate = () => {
    putControl({
      control_date: editDate,
      entreprise_id: editEntId,
      entreprise_nom: editEntNom,
      scores: buildScoresPayload(),
      theme_comments: themeComments,
      criterion_comments: criterionComments,
      status: 'validated',
    });
  };

  const revertControlToDraft = async (controlId) => {
    if (!controlId) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${apiBase}/admin/qc-controls/${controlId}`,
        { status: 'draft' },
        { headers: authHeaders }
      );
      if (selectedControlId === controlId) {
        setDetail(data);
        syncEditorFromDetail(data);
      }
      toast.success('Contrôle repassé en brouillon');
      await loadControls();
      if (viewModalId === controlId) {
        setViewModalId(null);
        setViewModalDetail(null);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleRevertDraft = () => {
    void revertControlToDraft(selectedControlId);
  };

  const openDeleteDialog = (id, label) => {
    setDeleteTargetId(id);
    setDeleteLabel(label || 'ce brouillon');
    setDeleteOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (!deleteTargetId) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/admin/qc-controls/${deleteTargetId}`, {
        headers: authHeaders,
      });
      toast.success('Brouillon supprimé');
      if (selectedControlId === deleteTargetId) {
        goList();
      }
      setDeleteOpen(false);
      setDeleteTargetId(null);
      setDeleteLabel('');
      await loadControls();
    } catch (e) {
      handleApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const openPdfExportDialog = (c) => {
    setPdfExportControl({
      id: c.id,
      companyName: c.entreprise_nom_snapshot || '—',
    });
    setPdfGlobalComment('');
    setPdfIncludeCriteriaTable(true);
    setPdfExportOpen(true);
  };

  const handlePdfExport = async () => {
    if (!pdfExportControl?.id) return;
    setPdfExporting(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/qc-controls/${pdfExportControl.id}/export-pdf`,
        {
          global_comment: pdfGlobalComment.trim(),
          include_criteria_table: pdfIncludeCriteriaTable,
        },
        {
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const raw = pdfExportControl.companyName || 'controle';
      const safe = String(raw)
        .replace(/[^\w\-.]+/g, '_')
        .slice(0, 64);
      a.download = `controle_qualite_${safe || 'export'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
      setPdfExportOpen(false);
      setPdfExportControl(null);
    } catch (e) {
      handleApiError(e);
    } finally {
      setPdfExporting(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!newFormId) {
      toast.error('Choisissez un formulaire');
      return;
    }
    if (!newDate) {
      toast.error('Indiquez la date du contrôle');
      return;
    }
    if (!newEntId) {
      toast.error('Choisissez une entreprise');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/qc-controls`,
        {
          form_id: newFormId,
          control_date: newDate,
          entreprise_id: newEntId,
          entreprise_nom: newEntNom || sitesList.find((s) => s.id === newEntId)?.nom || '',
        },
        { headers: authHeaders }
      );
      setSelectedControlId(data.id);
      setDetail(data);
      syncEditorFromDetail(data);
      setMode('edit');
      toast.success('Brouillon créé');
      await loadControls();
    } catch (e) {
      handleApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const goList = () => {
    setSelectedControlId(null);
    setDetail(null);
    setMode('list');
  };

  const goNew = () => {
    setSelectedControlId(null);
    setDetail(null);
    setMode('new');
    setNewFormId(formsList[0]?.id || '');
    setNewDate(todayISODate());
    setNewEntId('');
    setNewEntNom('');
  };

  const isValidated = detail?.status === 'validated';
  const readOnly = isValidated;
  const formThemes = detail?.form?.themes || [];
  const showDraftActionBar =
    mode === 'edit' && detail && !detailLoading && !readOnly;

  const setScore = (critId, val) => {
    setScores((prev) => {
      const next = { ...prev };
      if (val === '' || val === undefined || val === '__unset__') delete next[critId];
      else next[critId] = Number(val);
      return next;
    });
  };

  if (!isActive) return null;

  return (
    <div className="space-y-6 sm:space-y-8 w-full min-w-0">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 w-full min-w-0">
      <Card
        className="lg:col-span-4 border shadow-sm rounded-2xl"
        style={surfaceStyle}
      >
        <CardHeader
          className="space-y-0 pb-4 border-b"
          style={{ borderColor: theme.border }}
        >
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-lg" style={{ color: theme.text }}>
              Brouillons
            </CardTitle>
            <Button
              size="sm"
              className="rounded-lg bg-[#066183] hover:bg-[#054d6b]"
              onClick={goNew}
            >
              <Plus className="h-4 w-4 mr-1" />
              Nouveau
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-5 max-h-[min(70vh,520px)] overflow-y-auto">
          {listLoading ? (
            <p className="text-sm py-4" style={{ color: theme.textSecondary }}>
              Chargement…
            </p>
          ) : draftControls.length === 0 ? (
            <p className="text-sm py-4" style={{ color: theme.textSecondary }}>
              Aucun brouillon. Les contrôles validés figurent dans le tableau ci-dessous. Créez un
              brouillon avec « Nouveau ».
            </p>
          ) : (
            draftControls.map((c) => {
              const isDraft = c.status !== 'validated';
              return (
                <div
                  key={c.id}
                  className={`flex items-stretch gap-1 rounded-xl border transition-colors ${
                    selectedControlId === c.id
                      ? 'ring-2 ring-[#066183] ring-offset-2 ring-offset-background'
                      : ''
                  }`}
                  style={{
                    borderColor: theme.border,
                    backgroundColor:
                      selectedControlId === c.id ? theme.background : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedControlId(c.id);
                      setMode('edit');
                    }}
                    className="flex-1 min-w-0 text-left p-3 rounded-xl hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className="font-medium truncate flex-1"
                        style={{ color: theme.text }}
                      >
                        {c.form_name_snapshot || 'Formulaire'}
                      </p>
                      <Badge
                        variant={c.status === 'validated' ? 'default' : 'secondary'}
                        className={
                          c.status === 'validated'
                            ? 'bg-emerald-700 hover:bg-emerald-700 shrink-0'
                            : 'shrink-0'
                        }
                      >
                        {c.status === 'validated' ? 'Validé' : 'Brouillon'}
                      </Badge>
                    </div>
                    <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                      {c.control_date} · {c.entreprise_nom_snapshot || '—'}
                    </p>
                  </button>
                  {isDraft && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-auto min-h-[3rem] rounded-l-none rounded-r-xl text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                      title="Supprimer le brouillon"
                      disabled={saving}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openDeleteDialog(
                          c.id,
                          `${c.form_name_snapshot || 'Contrôle'} · ${c.control_date || ''}`
                        );
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card
        className="lg:col-span-8 border shadow-sm rounded-2xl"
        style={surfaceStyle}
      >
        <CardHeader className="border-b" style={{ borderColor: theme.border }}>
          <div className="flex flex-wrap items-center gap-3">
            {(mode === 'edit' || mode === 'new') && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg"
                style={{ borderColor: theme.border }}
                onClick={goList}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Liste
              </Button>
            )}
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <CardTitle style={{ color: theme.text }}>
                {mode === 'new'
                  ? 'Nouveau contrôle'
                  : mode === 'edit'
                    ? detail?.form_name_snapshot || 'Contrôle'
                    : 'Sélectionnez un contrôle'}
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent
          className={`space-y-6 pt-6 ${
            showDraftActionBar ? 'pb-28 sm:pb-32' : ''
          }`}
        >
          {mode === 'list' && (
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Choisissez un <strong>brouillon</strong> à gauche ou « Nouveau » pour en créer un.
              Les contrôles <strong>validés</strong> sont listés dans le tableau sous cette zone
              (formulaire, date, entreprise — même périmètre que le scan).
            </p>
          )}

          {mode === 'new' && (
            <div className="space-y-4 w-full max-w-none">
              <div className="space-y-2">
                <Label style={{ color: theme.text }}>Formulaire</Label>
                <Select value={newFormId} onValueChange={setNewFormId}>
                  <SelectTrigger
                    className="rounded-lg"
                    style={{ borderColor: theme.border }}
                  >
                    <SelectValue placeholder="Choisir un formulaire" />
                  </SelectTrigger>
                  <SelectContent>
                    {formsList.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qc-new-date" style={{ color: theme.text }}>
                  Date du contrôle
                </Label>
                <Input
                  id="qc-new-date"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="rounded-lg"
                  style={{ borderColor: theme.border }}
                />
              </div>
              <div className="space-y-2">
                <Label style={{ color: theme.text }}>Entreprise</Label>
                <Select
                  value={newEntId}
                  onValueChange={(v) => {
                    setNewEntId(v);
                    const s = sitesList.find((x) => x.id === v);
                    setNewEntNom(s?.nom || '');
                  }}
                >
                  <SelectTrigger
                    className="rounded-lg"
                    style={{ borderColor: theme.border }}
                  >
                    <SelectValue placeholder="Choisir une entreprise" />
                  </SelectTrigger>
                  <SelectContent>
                    {sitesList.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                className="rounded-lg bg-[#066183] hover:bg-[#054d6b]"
                onClick={handleCreateDraft}
                disabled={saving || formsList.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer le brouillon
              </Button>
              {formsList.length === 0 && (
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  Créez d’abord un formulaire dans l’onglet « Gestion formulaires ».
                </p>
              )}
            </div>
          )}

          {mode === 'edit' && detailLoading && (
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Chargement…
            </p>
          )}

          {mode === 'edit' && !detailLoading && detail && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qc-edit-date" style={{ color: theme.text }}>
                    Date du contrôle
                  </Label>
                  <Input
                    id="qc-edit-date"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    disabled={readOnly}
                    className="rounded-lg"
                    style={{ borderColor: theme.border }}
                  />
                </div>
                <div className="space-y-2">
                  <Label style={{ color: theme.text }}>Entreprise</Label>
                  <Select
                    value={editEntId}
                    onValueChange={(v) => {
                      setEditEntId(v);
                      const s = sitesList.find((x) => x.id === v);
                      setEditEntNom(s?.nom || '');
                    }}
                    disabled={readOnly}
                  >
                    <SelectTrigger
                      className="rounded-lg"
                      style={{ borderColor: theme.border }}
                    >
                      <SelectValue placeholder="Entreprise" />
                    </SelectTrigger>
                    <SelectContent>
                      {sitesList.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isValidated && (
                <div
                  className="rounded-lg border px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2"
                  style={{ borderColor: theme.border, color: theme.textSecondary }}
                >
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Contrôle validé — remettez en brouillon pour modifier les notes ou commentaires.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={saving}
                    onClick={handleRevertDraft}
                  >
                    <Undo2 className="h-4 w-4 mr-1" />
                    Remettre en brouillon
                  </Button>
                </div>
              )}

              {!detail.form && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Le formulaire source a été supprimé : les données enregistrées sont conservées mais
                  la structure n’est plus affichée.
                </p>
              )}

              {formThemes.map((th) => (
                <div
                  key={th.id}
                  className="rounded-xl border p-4 space-y-3"
                  style={{
                    borderColor: theme.border,
                    backgroundColor: theme.background,
                  }}
                >
                  <h3 className="font-semibold" style={{ color: theme.text }}>
                    {th.name || 'Thème'}
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-xs" style={{ color: theme.textSecondary }}>
                      Commentaire sur le thème
                    </Label>
                    <Textarea
                      value={themeComments[th.id] || ''}
                      onChange={(e) =>
                        setThemeComments((prev) => ({
                          ...prev,
                          [th.id]: e.target.value,
                        }))
                      }
                      disabled={readOnly}
                      rows={2}
                      className="rounded-lg resize-y min-h-[56px]"
                      style={{ borderColor: theme.border }}
                      placeholder="Observations sur ce thème…"
                    />
                  </div>
                  {(th.criteria || []).length > 0 && (
                    <div
                      className="rounded-lg border overflow-hidden"
                      style={{ borderColor: theme.border }}
                    >
                      <Table>
                        <TableHeader>
                          <TableRow
                            className="hover:bg-transparent"
                            style={{ borderColor: theme.border }}
                          >
                            <TableHead style={{ color: theme.text }}>Critère</TableHead>
                            <TableHead className="min-w-[180px]" style={{ color: theme.text }}>
                              Note (0 à 3)
                            </TableHead>
                            <TableHead style={{ color: theme.text }}>Commentaire</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(th.criteria || []).map((cr) => (
                            <TableRow key={cr.id} style={{ borderColor: theme.border }}>
                              <TableCell
                                className="align-top font-medium"
                                style={{ color: theme.text }}
                              >
                                {cr.name || '—'}
                              </TableCell>
                              <TableCell className="align-top">
                                <div
                                  className="flex flex-wrap items-center gap-1.5"
                                  role="group"
                                  aria-label={`Note pour ${cr.name || 'critère'}`}
                                >
                                  <button
                                    type="button"
                                    disabled={readOnly}
                                    onClick={() => setScore(cr.id, '__unset__')}
                                    title="Aucune note (brouillon)"
                                    className={`flex h-9 min-w-[2.25rem] items-center justify-center rounded-md border text-xs font-medium transition-colors ${
                                      scores[cr.id] === undefined || scores[cr.id] === ''
                                        ? 'border-[#066183] bg-[#066183]/10 text-[#066183]'
                                        : 'opacity-70 hover:opacity-100'
                                    }`}
                                    style={{ borderColor: theme.border }}
                                  >
                                    —
                                  </button>
                                  {[0, 1, 2, 3].map((n) => {
                                    const selected = Number(scores[cr.id]) === n;
                                    return (
                                      <button
                                        key={n}
                                        type="button"
                                        disabled={readOnly}
                                        title={`Note ${n}`}
                                        onClick={() => setScore(cr.id, String(n))}
                                        className={`flex h-9 min-w-[2.5rem] items-center justify-center rounded-md border text-sm font-semibold transition-colors ${
                                          selected
                                            ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                                            : 'hover:bg-muted/60'
                                        }`}
                                        style={
                                          selected
                                            ? undefined
                                            : { borderColor: theme.border, color: theme.text }
                                        }
                                        aria-pressed={selected}
                                      >
                                        {n}
                                      </button>
                                    );
                                  })}
                                </div>
                              </TableCell>
                              <TableCell className="align-top">
                                <Textarea
                                  value={criterionComments[cr.id] || ''}
                                  onChange={(e) =>
                                    setCriterionComments((prev) => ({
                                      ...prev,
                                      [cr.id]: e.target.value,
                                    }))
                                  }
                                  disabled={readOnly}
                                  rows={2}
                                  className="min-h-[56px] rounded-md resize-y"
                                  style={{ borderColor: theme.border }}
                                  placeholder="Commentaire…"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </div>

      <Card className="border shadow-sm rounded-2xl" style={surfaceStyle}>
        <CardHeader className="border-b" style={{ borderColor: theme.border }}>
          <CardTitle className="text-lg" style={{ color: theme.text }}>
            Visites validées
          </CardTitle>
          <p className="text-sm mt-1 font-normal" style={{ color: theme.textSecondary }}>
            Historique des contrôles qualité validés. Les brouillons restent dans le panneau de gauche.
          </p>
        </CardHeader>
        <CardContent className="pt-5">
          {listLoading ? (
            <p className="text-sm py-4" style={{ color: theme.textSecondary }}>
              Chargement…
            </p>
          ) : validatedControls.length === 0 ? (
            <p className="text-sm py-4" style={{ color: theme.textSecondary }}>
              Aucune visite validée pour l’instant.
            </p>
          ) : (
            <div className="rounded-xl border overflow-x-auto" style={{ borderColor: theme.border }}>
              <Table>
                <TableHeader>
                  <TableRow style={{ borderColor: theme.border }}>
                    <TableHead style={{ color: theme.text }}>Date de visite</TableHead>
                    <TableHead style={{ color: theme.text }}>Entreprise</TableHead>
                    <TableHead className="text-right" style={{ color: theme.text }}>
                      Note globale
                    </TableHead>
                    <TableHead className="min-w-[200px] text-center" style={{ color: theme.text }}>
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validatedControls.map((c) => (
                    <TableRow key={c.id} style={{ borderColor: theme.border }}>
                      <TableCell className="tabular-nums" style={{ color: theme.text }}>
                        {formatVisitDate(c.control_date)}
                      </TableCell>
                      <TableCell style={{ color: theme.text }}>
                        {c.entreprise_nom_snapshot || '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium" style={{ color: theme.text }}>
                        {formatNoteCell(c)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-lg"
                            style={{ borderColor: theme.border }}
                            onClick={() => setViewModalId(c.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ouvrir
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-lg"
                            style={{ borderColor: theme.border, color: theme.text }}
                            onClick={() => openPdfExportDialog(c)}
                          >
                            <FileDown className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {showDraftActionBar && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 sm:px-4"
          style={{
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            paddingTop: '0.5rem',
          }}
        >
          <div
            className="pointer-events-auto flex w-full max-w-4xl flex-wrap items-center justify-center gap-2 rounded-2xl border px-3 py-2.5 shadow-lg backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-3"
            style={{
              backgroundColor: theme.surface,
              borderColor: theme.border,
              boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
            }}
          >
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: theme.border, color: theme.text }}
              onClick={handleSaveDraft}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              Enregistrer le brouillon
            </Button>
            <Button
              type="button"
              className="rounded-lg bg-emerald-700 hover:bg-emerald-800"
              onClick={handleValidate}
              disabled={saving}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Valider officiellement
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/40"
              onClick={() =>
                openDeleteDialog(
                  selectedControlId,
                  `${detail?.form_name_snapshot || 'Contrôle'} · ${editDate || ''}`
                )
              }
              disabled={saving}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={pdfExportOpen}
        onOpenChange={(o) => {
          if (!o) {
            setPdfExportOpen(false);
            setPdfExportControl(null);
          }
        }}
      >
        <DialogContent
          className="max-w-lg rounded-2xl"
          style={{ backgroundColor: theme.surface, borderColor: theme.border }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: theme.text }}>Exporter en PDF</DialogTitle>
            <DialogDescription style={{ color: theme.textSecondary }}>
              {pdfExportControl
                ? `Contrôle qualité — ${pdfExportControl.companyName}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label style={{ color: theme.text }}>
                Commentaire global de l&apos;évaluation
              </Label>
              <Textarea
                value={pdfGlobalComment}
                onChange={(e) => setPdfGlobalComment(e.target.value)}
                rows={4}
                className="rounded-lg resize-y min-h-[100px]"
                style={{ borderColor: theme.border }}
                placeholder="Synthèse, remarques globales…"
              />
            </div>
            <div
              className="flex items-center justify-between gap-3 rounded-xl border p-3"
              style={{ borderColor: theme.border }}
            >
              <div className="space-y-0.5 pr-2 text-left">
                <p className="text-sm font-medium" style={{ color: theme.text }}>
                  Voir le tableau des résultats précis
                </p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  Critères, notes sur 3 et commentaires détaillés
                </p>
              </div>
              <Switch
                checked={pdfIncludeCriteriaTable}
                onCheckedChange={setPdfIncludeCriteriaTable}
              />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: theme.border }}
              onClick={() => {
                setPdfExportOpen(false);
                setPdfExportControl(null);
              }}
              disabled={pdfExporting}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="rounded-lg bg-[#066183] hover:bg-[#054d6b] text-white"
              onClick={() => void handlePdfExport()}
              disabled={pdfExporting}
            >
              <FileDown className="h-4 w-4 mr-2" />
              {pdfExporting ? 'Génération…' : 'Télécharger le PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          setDeleteOpen(o);
          if (!o) {
            setDeleteTargetId(null);
            setDeleteLabel('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive : {deleteLabel}. Les contrôles validés ne peuvent pas être
              supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annuler</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={saving}
              onClick={() => void confirmDeleteDraft()}
            >
              Supprimer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!viewModalId}
        onOpenChange={(o) => {
          if (!o) {
            setViewModalId(null);
            setViewModalDetail(null);
          }
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[min(90vh,720px)] overflow-y-auto sm:max-w-3xl"
          style={{ backgroundColor: theme.surface, borderColor: theme.border }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: theme.text }}>
              {viewModalLoading
                ? 'Chargement…'
                : viewModalDetail?.form_name_snapshot || 'Contrôle qualité'}
            </DialogTitle>
            <DialogDescription style={{ color: theme.textSecondary }}>
              Détail du contrôle validé (lecture seule).
            </DialogDescription>
          </DialogHeader>
          {viewModalLoading && (
            <p className="text-sm py-6" style={{ color: theme.textSecondary }}>
              Chargement du contrôle…
            </p>
          )}
          {!viewModalLoading && viewModalDetail && (
            <ValidatedControlReadOnly detail={viewModalDetail} theme={theme} />
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between sm:gap-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-lg w-full sm:w-auto"
              style={{ borderColor: theme.border, color: theme.text }}
              disabled={saving || viewModalLoading || !viewModalId}
              onClick={() => void revertControlToDraft(viewModalId)}
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Remettre en brouillon
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg w-full sm:w-auto"
              style={{ borderColor: theme.border }}
              onClick={() => {
                setViewModalId(null);
                setViewModalDetail(null);
              }}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControleQualiteTab;
